import { Link } from "react-router-dom";
import { 
  Mail, Phone, MapPin, Twitter, Linkedin, Instagram, Youtube, Facebook
} from "lucide-react";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FOOTER_LINKS = {
  product: [
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
    { label: "Integrations", href: "/integrations" },
    { label: "API", href: "/api-docs" }
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Careers", href: "/careers" },
    { label: "Press", href: "/press" }
  ],
  legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "Cookie Policy", href: "/cookies" },
    { label: "GDPR", href: "/gdpr" }
  ]
};

const SOCIAL_LINKS = [
  { icon: Twitter, href: "https://x.com/MarketingY42357", label: "Twitter" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/yasno-marketing/", label: "LinkedIn" },
  { icon: Instagram, href: "https://www.instagram.com/yasnomktg", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@mohamedabbas2090", label: "YouTube" },
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61586301077370", label: "Facebook" }
];

const EMAILS = [
  { email: "info@yasnomktg.com" },
  { email: "support@yasnomktg.com" },
  { email: "sales@yasnomktg.com" },
  { email: "marketing@yasnomktg.com" }
];

/**
 * Footer Component
 * Full footer with correct contact info - ORIGINAL DESIGN
 */
export default function Footer() {
  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const el = document.getElementById(href.replace('#', ''));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="py-16 px-4 sm:px-6 lg:px-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src="/logo.png" alt="YASNO" className="w-10 h-10 rounded-xl" />
              <span className="text-xl font-bold">
                <span className="text-white">YAS</span>
                <span className="text-primary">NO</span>
              </span>
            </Link>
            <p className="text-sm text-muted-foreground mb-6">
              The AI-powered marketing automation platform that speaks your language. 
              Launch campaigns with just your voice.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3" data-testid="footer-social-links">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-3">
              {FOOTER_LINKS.product.map((link) => (
                <li key={link.label}>
                  {link.href.startsWith('#') ? (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {FOOTER_LINKS.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {FOOTER_LINKS.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info - Emails Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-t border-white/10">
          {EMAILS.map(({ email }) => (
            <div key={email} className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <a href={`mailto:${email}`} className="text-sm text-primary hover:underline">
                {email}
              </a>
            </div>
          ))}
        </div>

        {/* Contact Info - Phone & Address */}
        <div className="flex flex-wrap items-center gap-6 py-6 border-t border-white/10">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <a href="tel:+13025202688" className="text-sm">
              +1 (302) 520-2688
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">
              Zokak Al Balat, Said Chartouni Street, Beirut, Lebanon
            </span>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} YASNO Marketing LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Powered by</span>
            <span className="font-semibold text-foreground">GPT-5.2</span>
            <span>•</span>
            <span className="font-semibold text-foreground">Sora 2</span>
            <span>•</span>
            <span className="font-semibold text-foreground">ElevenLabs</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
